package one.healthium.update_food_preferences.ui

import co.touchlab.kermit.Logger
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.update
import one.healthium.ui.common.event_producer.EventProducer
import one.healthium.ui.common.message.UiMessage
import one.healthium.ui.common.message.UiMessageManager
import one.healthium.ui.common.vm.BaseViewModel
import one.healthium.ui.components.question.AnswerStatus
import one.healthium.ui.components.question.AnswerUiModel
import one.healthium.ui.components.question.QuestionUiModel
import one.healthium.update_food_preferences.domain.FoodPreferenceInteractor
import one.healthium.update_food_preferences.domain.models.FoodPreferenceCategoryOptions
import one.healthium.update_food_preferences.domain.models.FoodPreferenceOption
import one.healthium.update_food_preferences.domain.models.FoodPreferenceStatus

internal class UpdateFoodPreferencesViewModel(
    private val foodPreferenceInteractor: FoodPreferenceInteractor,
) : BaseViewModel() {

    private val _state = MutableStateFlow(UpdateFoodPreferencesUiState())
    val state = _state.asStateFlow()

    val uiMessage = UiMessageManager()
    val events = EventProducer<UpdateFoodPreferencesEvent>()

    init {
        foodPreferenceInteractor.getUserFoodPreferences()
            .onStart {
                _state.update { state -> state.copy(loading = true) }
            }
            .onEach { userFoodPreferences ->
                _state.update { state ->
                    state.copy(
                        loading = false,
                        foodPreferences = userFoodPreferences,
                    )
                }
            }
            .catch {
                Logger.e("Error loading food preferences", it)
                uiMessage.add(UiMessage.throwable(it))
            }
            .launch()
    }

    fun onAnswerSelected(question: QuestionUiModel, answer: AnswerUiModel) {
        _state.update { currentState ->
            val answersForQuestion = currentState.selectedAnswers[question.id].orEmpty()

            val updatedStatus = answer.status.next(question.allowedStatuses)
            val updatedAnswersForQuestion = answersForQuestion.filterNot { it.first.id == answer.id } + (answer to updatedStatus)
            val updatedSelectedAnswers = currentState.selectedAnswers + (question.id to updatedAnswersForQuestion)
            currentState.copy(selectedAnswers = updatedSelectedAnswers)
        }
    }

    fun onSaveClicked() {
        launchGlobal {
            val currentState = _state.value
            val selectedAnswers = currentState.selectedAnswers

            val updatedUserFoodPreferences = selectedAnswers.map { (categoryId, answers) ->
                FoodPreferenceCategoryOptions(
                    id = categoryId,
                    options = answers.orEmpty().map { (answer, status) ->
                        FoodPreferenceOption(
                            id = answer.id,
                            status = status.toFoodPreferenceStatus()
                        )
                    },
                )
            }

            if (updatedUserFoodPreferences.isEmpty()) {
                Logger.d("No changes in food preferences, skipping update")
                events.trigger(UpdateFoodPreferencesEvent.OnFoodPreferencesSaved)
                return@launchGlobal
            }

            _state.update { it.copy(saveInProgress = true) }
            try {
                foodPreferenceInteractor.updateUserFoodPreferences(updatedUserFoodPreferences)
                events.trigger(UpdateFoodPreferencesEvent.OnFoodPreferencesSaved)
            } catch (e: Exception) {
                Logger.e("Error updating user food preferences", e)
                uiMessage.add(UiMessage.throwable(e))
            } finally {
                _state.update { it.copy(saveInProgress = false) }
            }
        }
    }
}

private fun AnswerStatus.toFoodPreferenceStatus(): FoodPreferenceStatus {
    return when (this) {
        AnswerStatus.Included -> FoodPreferenceStatus.Included
        AnswerStatus.Excluded -> FoodPreferenceStatus.Excluded
        AnswerStatus.None -> FoodPreferenceStatus.Neutral
    }
}
